import React, { memo, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, Typography, Button, Icon } from 'investira.react.components';
import { validators } from 'investira.sdk';
import { Upload, ArquivosDataGrid, BulkContextActions } from '../../components/organisms';
import { FooterTemplate, PageTemplate } from '../../components/templates';
import { useUploadContext } from '../../contexts/UploadContext';
import services from '../../services';
import withResponseHandling from '../../hoc/withResponseHandling';
import withMessage from '../../hoc/withMessage';

import { acBackgroundArquivosChanged } from '../../store/actions';

const PAGE_SIZE = 50;

const ArquivosRoot = memo(props => {
  const updatedAt = useSelector(state => state.background.arquivos.updatedAt);
  const entidadeId = useSelector(state => state.entidade.current.default_entidade_id);
  const dispatch = useDispatch();

  const [arquivos, setArquivos] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [rowSelection, setRowSelection] = useState({});

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE
  });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([{ id: 'origem', value: 'Manual' }]);

  const readArquivos = useCallback(
    (pParams = {}) => {
      if (!arquivos.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      const mapSortId = pId => {
        switch (pId) {
          default:
            return pId;
        }
      };
      // Mapeamento dos valores de filtro para 'origem'
      const mapFilterValue = (pId, pValue) => {
        if (pId === 'origem') {
          const mapping = {
            Manual: 'MAN',
            Bot: 'BOT'
          };
          return mapping[pValue] || pValue;
        }
        return pValue;
      };

      const xFilters = {};
      console.log('columnFilters', columnFilters);

      (columnFilters || []).forEach(xFilter => {
        if (xFilter?.value !== undefined && xFilter?.value !== null && xFilter?.value !== '') {
          console.log('xFilter', xFilter);
          const xKey = mapSortId(xFilter.id);
          console.log('xKey', xKey);
          xFilters[xKey] = mapFilterValue(xFilter.id, xFilter.value);
        }
      });

      console.log('xFilters', xFilters);

      services.posicoes.arquivos(
        {
          params: {
            sort: 'data_envio desc',
            size: PAGE_SIZE,
            page: 1,
            ...xFilters,
            ...pParams
          }
        },
        rRes => {
          setArquivos(Object.values(rRes.data));
          setRowCount(rRes.pages.total_items);
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          setIsLoading(false);
          setIsRefetching(false);
        }
      );
    },
    [arquivos.length, props, sorting, columnFilters]
  );

  const deleteArquivos = (pFile, onFinish) => {
    const { data_envio, arquivo } = pFile;
    services.posicoes.uploadRemove(
      { data_envio, arquivo },
      rRes => {
        props.onMessageSuccess('Arquivo excluído com sucesso');
        readArquivos();
        setRowSelection({});
      },
      rErr => {
        props.responseErrorHandling(rErr);
      },
      () => {
        onFinish && onFinish();
      }
    );
  };

  const bulkDeleteArquivos = pRows => {
    const xArquivosId = pRows.map(xRow => {
      const { data_envio, arquivo, entidade_id } = xRow.original;
      return { data_envio, arquivo, entidade_id };
    });

    xArquivosId.forEach(xArquivo => {
      deleteArquivos(xArquivo);
    });
  };

  const uploadContext = useUploadContext();

  const handleUploadClick = () => {
    uploadContext.actions.open();
  };

  const handleFinishUpload = () => {
    readArquivos();
    dispatch(acBackgroundArquivosChanged(true));
  };

  const handleDeleteClick = pFileData => {
    setFile(pFileData);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFile(null);
  };

  useEffect(() => {
    const xPage = pagination.pageIndex + 1;
    readArquivos({
      page: xPage,
      size: pagination.pageSize
    });
  }, [
    readArquivos,
    pagination.pageIndex,
    pagination.pageSize,
    updatedAt,
    entidadeId,
    sorting,
    columnFilters
  ]);

  useEffect(() => {
    if (!validators.isEmpty(file)) {
      handleClickOpen();
    }
  }, [file]);

  return (
    <PageTemplate
      title="Uploads"
      description="Você pode enviar arquivos XML conforme os padrões ANBIMA 4 e 5."
      scrollable={true}
      headerRenderActions={
        <Button
          disabled={props.disabled}
          variant="contained"
          color="primary"
          startIcon={<Icon color="secondary" iconName="upload" size={16} />}
          size="small"
          sx={{ px: 2 }}
          onClick={handleUploadClick}>
          Selecionar arquivos
        </Button>
      }>
      <Upload onFinish={handleFinishUpload}>
        <ArquivosDataGrid
          data={arquivos}
          isLoading={isLoading}
          initialState={{ pagination, sorting, columnFilters }}
          manualFiltering
          manualPagination
          manualSorting
          getRowId={row => row._rid}
          state={{
            isLoading,
            pagination,
            //sorting,
            columnFilters,
            showProgressBars: isRefetching,
            rowSelection
          }}
          rowCount={rowCount}
          //onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          onPaginationChange={setPagination}
          onDeleteClick={handleDeleteClick}
          onRowSelectionChange={setRowSelection}
          renderTopToolbarCustomActions={({ table }) => {
            const selectedRows = table.getSelectedRowModel().rows;
            const isSomeRowSelected = selectedRows.length > 0;

            return (
              <BulkContextActions
                onDeleteClick={() => {
                  bulkDeleteArquivos(selectedRows);
                }}
                disabled={isLoading || isRefetching || !isSomeRowSelected}
              />
            );
          }}
          renderEmptyRowsFallback={({ table }) => {
            return (
              <Stack py={6}>
                <Typography variant="body1" color="textPrimary" align="center">
                  Arraste e solte alguns arquivos aqui
                </Typography>
                <Typography component="p" variant="caption" color="textPrimary" align="center">
                  (Somente serão aceitos arquivos .xml)
                </Typography>
              </Stack>
            );
          }}
        />
      </Upload>
    </PageTemplate>
  );
});

ArquivosRoot.displayName = 'ArquivosRoot';

export default withMessage(withResponseHandling(ArquivosRoot));
